import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

const FlipBookView = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchAlbumData();
  }, [albumId]);

  useEffect(() => {
    // Reset and load new image when page changes
    setImageLoaded(false);
    if (photos[currentPage]) {
      loadImage(photos[currentPage].id);
    }
  }, [currentPage, photos]);

  useEffect(() => {
    // Handle slideshow
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentPage((prev) => {
          if (prev >= photos.length - 1) {
            setIsPlaying(false);
            return 0; // Loop back to start
          }
          return prev + 1;
        });
      }, 3000); // Change slide every 3 seconds
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, photos.length]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const fetchAlbumData = async () => {
    try {
      const [albumRes, photosRes] = await Promise.all([
        axios.get(`${API}/albums/${albumId}`),
        axios.get(`${API}/albums/${albumId}/photos`)
      ]);
      setAlbum(albumRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      toast.error('Failed to load album');
      navigate('/dashboard');
    }
  };

  const loadImage = async (photoId) => {
    try {
      // Fetch image with authentication via axios
      const response = await axios.get(`${API}/photos/${photoId}/image`, {
        responseType: 'blob'
      });
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(response.data);
      
      // Clean up previous blob URL
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
      
      setImageUrl(blobUrl);
    } catch (error) {
      console.error('Failed to load image:', photoId, error);
      setImageUrl(null);
      setImageLoaded(true);
    }
  };

  const nextPage = () => {
    if (currentPage < photos.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const toggleSlideshow = () => {
    setIsPlaying(!isPlaying);
  };

  const currentPhoto = photos[currentPage];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      <header className="bg-black/50 backdrop-blur-sm border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(`/album/${albumId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold">{album?.title}</h1>
          <div className="text-sm text-gray-400">
            {currentPage + 1} / {photos.length}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden min-h-[600px]">
          {currentPhoto ? (
            <div className="p-8">
              {!imageLoaded && !imageUrl && (
                <div className="w-full h-[500px] bg-gray-200 animate-pulse rounded-lg mb-6 flex items-center justify-center">
                  <div className="text-gray-500">Loading image...</div>
                </div>
              )}
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={currentPhoto.caption || currentPhoto.filename}
                  className={`w-full h-[500px] object-contain mb-6 rounded-lg bg-gray-100 ${!imageLoaded ? 'hidden' : ''}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={(e) => {
                    console.error('Image failed to load:', currentPhoto.id);
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="%23ddd"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Image not found</text></svg>';
                    setImageLoaded(true);
                  }}
                />
              ) : imageLoaded ? (
                <div className="w-full h-[500px] bg-gray-100 rounded-lg mb-6 flex items-center justify-center">
                  <div className="text-gray-500">Image not available</div>
                </div>
              ) : null}
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-gray-900">{currentPhoto.caption || 'Untitled'}</h2>
                {currentPhoto.location && (
                  <p className="text-gray-600">{currentPhoto.location}</p>
                )}
                {currentPhoto.original_date && (
                  <p className="text-sm text-gray-500">{currentPhoto.original_date}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[600px]">
              <div className="text-gray-400">No photos in this album</div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 mt-8">
          <Button
            onClick={prevPage}
            disabled={currentPage === 0 || isPlaying}
            size="lg"
            className="bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 disabled:opacity-50"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          
          <Button
            onClick={toggleSlideshow}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
          >
            {isPlaying ? (
              <>
                <Pause className="w-5 h-5 mr-2" />
                Pause Slideshow
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Start Slideshow
              </>
            )}
          </Button>
          
          <Button
            onClick={nextPage}
            disabled={currentPage === photos.length - 1 || isPlaying}
            size="lg"
            className="bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 disabled:opacity-50"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
        
        {photos.length > 0 && (
          <div className="mt-4 text-center">
            <p className="text-gray-400 text-sm">
              {isPlaying ? 'Slideshow playing - changes every 3 seconds' : 'Use arrow keys or click buttons to navigate'}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default FlipBookView;
